import React, { useState, useRef } from 'react';
import Dropzone from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import InlineHelpText from '../../shared/InlineHelpText';
import {
  base64StringtoFile,
  extractImageFileExtensionFromBase64,
  formMediaVersionedUrl,
} from '../../../utilities/imageUtilities';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
} from '../../shared/Button';
import HelpIcon from '../../shared/HelpIcon';
import {
  submitText,
  cancelText,
  clearText,
} from '../../shared/SharedInternationalizations';
import SVGUploadIcon from '../../svg/UploadIcon';
import Overlay from '../../shared/Overlay';

function AddEditUserImageForm(props) {
  const { user, onSubmit, previous } = props;
  const [imageSource, setImageSource] = useState(null);
  const [imageExtension, setImageExtension] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const [imageCrop, setImageCrop] = useState({ unit: '%', width: 90, aspect: 1 });
  const [imageUrl, setImageUrl] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [initialImage, setInitialImage] = useState(user.photo);
  const [showSubmitEmpty, setShowSubmitEmpty] = useState(false);
  const [existingImage, setExistingImage] = useState(false);
  const [completedCrop, setCompletedCrop] = useState(null);

  const imageRef = useRef();

  const [showDropHelpText, setShowDropHelpText] = useState(false);
  const dropHelpTextRef = useRef();
  function toggleDropHelpText(value) {
    setShowDropHelpText(value);
  }
  const dropHelpText = (
    <FormattedMessage
      id="addEditUserImageForm.dropHelpText"
      defaultMessage="Drag and drop an image into the box to upload an image. Clicking on the box will also open a file upload window."
      description="Help text."
    />
  );
  const [showCropHelpText, setShowCropHelpText] = useState(false);
  const cropHelpTextRef = useRef();
  function toggleCropHelpText(value) {
    setShowCropHelpText(value);
  }
  const cropHelpText = (
    <FormattedMessage
      id="addEditUserImageForm.cropHelpText"
      defaultMessage="Click and drag to crop the image."
      description="Help text."
    />
  );

  const imageMaxSize = 2000000; // bytes
  const acceptedFileTypes =
    'image/x-png, image/png, image/jpg, image/jpeg, image/gif';
  const acceptedFileTypesArray = acceptedFileTypes.split(',').map((item) => {
    return item.trim();
  });

  function checkFile(files) {
    if (files && files.length > 0) {
      const image = files[0];
      if (image.size > imageMaxSize) {
        //image too big
        return false;
      }
      if (!acceptedFileTypesArray.includes(image.type)) {
        // wrong file type
        return false;
      }
      return true;
    }
  }

  function handleFileSelect(files) {
    if (files && files.length > 0) {
      const checkedFile = checkFile(files);
      if (checkedFile) {
        const image = files[0];
        const rawName = image.path;
        const cleanedName = rawName.replace(/ /g, '_').replace(/\(|\)/g, '');

        const today = Date.now();
        setImageName(today + '_' + cleanedName);

        const fileReader = new FileReader();
        fileReader.addEventListener(
          'load',
          () => {
            const result = fileReader.result;
            const fileExtension = extractImageFileExtensionFromBase64(result);
            setImageSource(result);
            setImageExtension(fileExtension);
            setImageUrl(result);
          },
          false
        );
        fileReader.readAsDataURL(image);
        setShowCrop(true);
      }
    }
  }

  function handleExistingFile() {
    function fetchImage() {
      const myHeaders = new Headers();

      const myInit = {
        method: 'GET',
        headers: myHeaders,
        //mode: 'no-cors',
        cache: 'reload',
      };
      //const myRequest = new Request(user.photo.http_path, myInit);
      const myRequest = new Request(formMediaVersionedUrl(user.photo.http_path), myInit);

      fetch(myRequest)
        .then(function (response) {
          const img = response.url;
          const fileName_Index = img.lastIndexOf('/') + 1; // eslint-disable-next-line
          const fileName = img.substr(fileName_Index).replace(/[\#\?].*$/, '');
          const cleanedName = fileName.replace(/ /g, '_').replace(/\(|\)/g, '');
          const today = Date.now();
          setImageName(today + '_' + cleanedName);
          return response.blob();
        })
        .then(function (blob) {
          // here the image is a blob

          const fileReader = new FileReader();
          fileReader.addEventListener(
            'load',
            () => {
              const result = fileReader.result;
              const fileExtension = extractImageFileExtensionFromBase64(result);
              setImageSource(result);
              setImageExtension(fileExtension);
              setImageUrl(result);
            },
            false
          );
          setExistingImage(true);
          fileReader.readAsDataURL(blob);
          setShowCrop(true);
        });
    }
    fetchImage();
  }

  function imageLoaded(image) {
    imageRef.current = image;
  }

  function handleCropChange(crop, percentCrop) {
    setImageCrop(percentCrop);
  }
  
  function handleCropComplete(crop, percentCrop) {
    setCompletedCrop(crop);
    makeClientCrop(crop);
  }

  async function makeClientCrop(crop) {
    if (imageRef.current && crop.width && crop.height) {
      const croppedImageUrl = await getCroppedImg(
        imageRef.current,
        crop,
        'newFile'
      );
      setBlobUrl(croppedImageUrl);
    }
  }

  function getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    var originWidth = crop.width * scaleX;
    var originHeight = crop.height * scaleY;
    // maximum width/height
    var maxWidth = 1200,
      maxHeight = 1200;
    var targetWidth = originWidth,
      targetHeight = originHeight;
    if (originWidth > maxWidth || originHeight > maxHeight) {
      if (originWidth / originHeight > maxWidth / maxHeight) {
        targetWidth = maxWidth;
        targetHeight = Math.round(maxWidth * (originHeight / originWidth));
      } else {
        targetHeight = maxHeight;
        targetWidth = Math.round(maxHeight * (originWidth / originHeight));
      }
    }
    // set canvas size
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      targetWidth,
      targetHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        blob.name = fileName + '.' + imageExtension;

        const fileReader = new FileReader();
        fileReader.addEventListener(
          'load',
          () => {
            const result = fileReader.result;
            setImageUrl(result);
          },
          false
        );
        fileReader.readAsDataURL(blob);

        let fileUrl;
        window.URL.revokeObjectURL(fileUrl);
        fileUrl = window.URL.createObjectURL(blob);
        resolve(fileUrl);
      }, 1);
    });
  }

  function handleSubmit() {
    let image = null;
    if (imageUrl) {
      image = base64StringtoFile(imageUrl, imageName);
    }
    onSubmit(image);
  }

  function handleClear(event) {
    setInitialImage(null);
    setImageSource(null);
    setImageExtension(null);
    setImageUrl(null);
    setImageCrop({ unit: '%', width: 90, aspect: 1 });
    setCompletedCrop(null);
  }
  function showCropInterface() {
    setShowCrop(true);
  }
  function hideCropInterface() {
    setShowCrop(false);
  }

  function submitEmptyConfirmation() {
    setShowSubmitEmpty(true);
  }

  function cancelSubmitEmpty() {
    setShowSubmitEmpty(false);
  }

  const submitEmptyDialogText = (
    <FormattedMessage
      id="userImage.submitEmptyDialogText"
      defaultMessage="Please be aware: {br}Submitting this form without a user image will remove the user image from this user and from all associated devices which means it will no longer appear in alerts."
      values={{
        br: <br />,
      }}
      description="Text that displays when attempting to submit a user image that does not contain a user image"
    />
  );

  const submitEmptyConfirmationText = (
    <FormattedMessage
      id="userImage.submitEmptyConfirmationText"
      defaultMessage="Remove User Image"
      description="Link back to the users interface"
    />
  );

  function submitEmptyChallenge() {
    if (showSubmitEmpty) {
      return (
        <Overlay
          rootUrl={`/users/${user.id}/edit/?mode=userImageEdit`}
          canClose={false}
          data-test="user-archive-challenge-overlay"
          useFunction={true}
          onClick={cancelSubmitEmpty}>
          <div className="content-container content-container--column">
            <h1 className="no-margin text-center">
              {user.first_name} {user.last_name}
            </h1>
            {submitEmptyDialogText}
            <hr />
            <div className="flex-end">
              <Button
                onClick={cancelSubmitEmpty}
                text={cancelText}
                buttonType={buttonTypes.noButton}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin margin-right-15 js-cancel-archive-button"
              />
              <Button
                onClick={handleSubmit}
                text={submitEmptyConfirmationText}
                buttonType={buttonTypes.danger}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin js-submit-archive-button"
              />
            </div>
          </div>
        </Overlay>
      );
    } else {
      return '';
    }
  }

  return (
    <React.Fragment>
      {submitEmptyChallenge()}
      <div className="content-display__container" data-test="user-image">
        {imageSource !== null ? (
          <div
            className="content-display__column content-display__column"
            data-test="crop-interface">
            <div className="content-display__item-container">
              {showCrop ? (
                <div className="item-container__main-container">
                  <ReactCrop
                    crop={imageCrop}
                    onChange={handleCropChange}
                    onComplete={handleCropComplete}
                    aspect={1}
                    ruleOfThirds>
                    <img
                      ref={imageRef}
                      alt="Crop"
                      src={imageSource}
                      onLoad={(e) => imageLoaded(e.currentTarget)}
                    />
                  </ReactCrop>
                </div>
              ) : (
                <div className="user-image__image-container">
                  <img
                    alt="Crop"
                    src={blobUrl}
                    crossOrigin="Anonymous"
                    className="user-image__image-display"
                  />
                </div>
              )}
              <div className="item-container__info-container">
                <HelpIcon
                  onClick={() => cropHelpTextRef.current.toggleHelpText()}
                  toggled={showCropHelpText}
                  styles="margin-left-5"
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            className="content-display__column content-display__column"
            data-test="drop-interface">
            <div className="content-display__item-container">
              {initialImage && initialImage.http_path ? (
                handleExistingFile()
              ) : (
                <Dropzone
                  onDrop={(acceptedFiles) => {
                    handleFileSelect(acceptedFiles);
                  }}>
                  {({ getRootProps, getInputProps }) => (
                    <div
                      className="item-container__main-container dropzone-area text-large"
                      {...getRootProps()}>
                      {/* <div
                        style={{ border: "1px solid black" }}
                       
                      > */}
                      <input {...getInputProps()} />
                      <SVGUploadIcon />
                      <FormattedMessage
                        id="addEditUserImage.dropzoneText"
                        defaultMessage="Drag an image here or click to upload"
                        description="Clears something"
                      />
                    </div>
                  )}
                </Dropzone>
              )}

              <div className="item-container__info-container">
                <HelpIcon
                  onClick={() => dropHelpTextRef.current.toggleHelpText()}
                  toggled={showDropHelpText}
                  styles="margin-left-5"
                />
              </div>
            </div>
          </div>
        )}
        <div className="width-100">
          <InlineHelpText
            ref={dropHelpTextRef}
            toggleFunction={toggleDropHelpText}
            message={dropHelpText}
          />
          <InlineHelpText
            ref={cropHelpTextRef}
            toggleFunction={toggleCropHelpText}
            message={cropHelpText}
          />
        </div>
        <div className="form__section-container form__section-container--spread width-100">
          <div className="form__items-group-container">
            <Link
              className="link link-text"
              to={previous}
              data-test="user-image-form-cancel-link">
              {cancelText}
            </Link>
          </div>
          <div className="form__items-group-container">
            <div className="align-center">
              {imageSource !== null ? (
                <React.Fragment>
                  {showCrop ? (
                    <Button
                      text={'crop'}
                      buttonType={buttonTypes.blue}
                      buttonSize={buttonSizes.small}
                      onClick={hideCropInterface}
                      additionalStyles="no-margin margin-right-10"
                      disabled={!completedCrop}
                    />
                  ) : (
                    <Button
                      text={'re-crop'}
                      buttonType={buttonTypes.blue}
                      buttonSize={buttonSizes.small}
                      onClick={showCropInterface}
                      additionalStyles="no-margin margin-right-10"
                    />
                  )}
                  <Button
                    text={clearText}
                    buttonType={buttonTypes.blue}
                    buttonSize={buttonSizes.small}
                    onClick={handleClear}
                    additionalStyles="no-margin"
                  />
                </React.Fragment>
              ) : (
                ''
              )}
            </div>

            <div className="align-center">
              <Button
                // buttonFunction={buttonFunctions.submit}
                onClick={
                  imageSource === null && existingImage
                    ? submitEmptyConfirmation
                    : handleSubmit
                }
                text={submitText}
                buttonType={buttonTypes.success}
                buttonSize={buttonSizes.small}
                additionalStyles={'no-margin margin-left-15'}
                disabled={imageSource === null && !existingImage ? true : false}
              />
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default AddEditUserImageForm;
